// The hotdog (STORY_BIBLE §7): the plated frank in front of your stool, picked up and eaten.
// The wipe return smash-cuts to the bar, your cowboy picks it up off the plate and takes a bite,
// close enough to hear it. Later the player learns what the hotdogs are made of; this is the shot
// that has to come back to them.
//
// Once picked up the frank rides the cowboy's right palm (CowboyView.boneWorld: the wrist, pushed on
// along the forearm), its front end turned toward the mouth. The director walks the hand plate ->
// held -> mouth (CowboyRS.reach*) and calls bite() at the top of each lift: a world-space clipping
// plane steps in along the frank, with a cap so the cut end reads as bread and meat rather than a
// hollow tube.

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

const BASE = (import.meta as unknown as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? '/';
const LEN = 0.2; // the frank, end to end
const BITE = 0.034; // what one bite takes
const PALM = 0.07; // wrist to the middle of the palm, along the forearm
const AHEAD = 0.05; // the frank's centre sits this far in front of the palm (you hold the back end)
const X = new THREE.Vector3(1, 0, 0);

const _m = new THREE.Matrix4();
const _p = new THREE.Vector3(), _q = new THREE.Quaternion(), _q2 = new THREE.Quaternion(), _q3 = new THREE.Quaternion(), _s = new THREE.Vector3();
const _a = new THREE.Vector3(), _b = new THREE.Vector3(), _h = new THREE.Vector3();

export class Hotdog {
  readonly plate = new THREE.Group();
  /** the frank: a pivot centred on it, its long axis on local X */
  readonly frank = new THREE.Group();
  private loaded = false;
  private mats: THREE.Material[] = [];
  private clip = new THREE.Plane(new THREE.Vector3(1, 0, 0), 1e3);
  private cap: THREE.Mesh;
  private onPlate = new THREE.Matrix4();
  /** 0 on the plate, 1 in the hand */
  private held = 0;
  private holding = false;
  bites = 0;
  get eating() { return this.frank.visible && this.holding; }
  /** the bites come off the front (+X) end, the one turned to the mouth */
  private end = 1;

  constructor(private parent: THREE.Object3D) {
    const plate = new THREE.Mesh(new THREE.CylinderGeometry(0.13, 0.1, 0.018, 28), new THREE.MeshStandardMaterial({ color: 0xece2cc, roughness: 0.35 }));
    plate.position.y = 0.009;
    this.plate.add(plate);
    this.plate.visible = false;
    this.frank.visible = false;
    // the bitten end: bread outside, a pink middle
    const cg = new THREE.CircleGeometry(0.026, 18);
    this.cap = new THREE.Mesh(cg, new THREE.MeshStandardMaterial({ color: 0xd9a87a, roughness: 0.9, side: THREE.DoubleSide }));
    const meat = new THREE.Mesh(new THREE.CircleGeometry(0.012, 14), new THREE.MeshStandardMaterial({ color: 0xa8483a, roughness: 0.7, side: THREE.DoubleSide }));
    meat.position.z = 0.0005;
    this.cap.add(meat);
    this.cap.visible = false;
    parent.add(this.plate);
    parent.add(this.frank);
    parent.add(this.cap);
  }

  private async load() {
    if (this.loaded) return;
    this.loaded = true;
    try {
      const g = await new GLTFLoader().loadAsync(BASE + 'assets/saloon/hotdog.glb');
      const box = new THREE.Box3().setFromObject(g.scene);
      const size = box.getSize(new THREE.Vector3());
      const c = box.getCenter(new THREE.Vector3());
      const s = LEN / Math.max(size.x, size.y, size.z);
      const inner = new THREE.Group();
      inner.add(g.scene);
      g.scene.position.set(-c.x, -c.y, -c.z);
      inner.scale.setScalar(s);
      // lay the long axis on X
      if (size.z > size.x) inner.rotation.y = Math.PI / 2;
      this.frank.add(inner);
      g.scene.traverse((o) => {
        const m = o as THREE.Mesh;
        if (!m.isMesh) return;
        const list = Array.isArray(m.material) ? m.material : [m.material];
        for (const mat of list) { mat.clippingPlanes = [this.clip]; mat.side = THREE.DoubleSide; this.mats.push(mat); }
        m.castShadow = false;
      });
    } catch { /* the plate on its own still says it */ }
  }

  /** Put it on the plate in front of a stool (car-local), or take it all away. */
  set(on: boolean, x = 0, y = 0, z = 0) {
    this.plate.visible = on; this.frank.visible = on;
    this.holding = false; this.held = 0; this.bites = 0;
    this.clip.constant = 1e3;
    this.cap.visible = false;
    if (!on) return;
    void this.load();
    this.plate.position.set(x, y, z);
    this.frank.position.set(x, y + 0.05, z);
    this.frank.quaternion.setFromEuler(new THREE.Euler(0, 0.5, 0));
    this.frank.scale.setScalar(1);
    this.frank.updateMatrix();
    this.onPlate.copy(this.frank.matrix);
  }

  /** the frank on the plate, world (where the hand reaches to pick it up) */
  plateWorld(out: THREE.Vector3) {
    this.parent.updateWorldMatrix(true, false);
    _m.copy(this.onPlate).premultiply(this.parent.matrixWorld);
    return out.setFromMatrixPosition(_m);
  }

  /** The hand has it: it leaves the plate for the palm over the next ~0.15 s. */
  pickUp() { this.holding = true; }
  /** Put it back down on the plate (what's left of it). */
  putDown() { this.holding = false; }
  /** One bite off the front end. */
  bite() { if (this.bites < 3) this.bites++; }

  /**
   * Per frame. `wrist`/`elbow`: the right hand and forearm bones' world positions this frame (null:
   * not drawn); `mouth` the world point it's eaten at; `lift` 0 held low (the frank lies nearly flat,
   * nose toward the face) .. 1 up at the mouth (nose straight in).
   */
  update(dt: number, wrist: THREE.Vector3 | null, elbow: THREE.Vector3 | null, mouth: THREE.Vector3 | null, lift: number) {
    if (!this.frank.visible) return;
    const inHand = this.holding && !!wrist && !!elbow && !!mouth;
    this.held += ((inHand ? 1 : 0) - this.held) * Math.min(1, dt * 14);
    const pp = _a, pq = _q, ps = _s;
    this.onPlate.decompose(pp, pq, ps);
    if (wrist && elbow && mouth && this.held > 0.001) {
      // the palm, a little on from the wrist along the forearm
      _p.subVectors(wrist, elbow).normalize().multiplyScalar(PALM).add(wrist);
      // the nose toward the mouth; held low it lies flatter
      _h.subVectors(mouth, _p);
      _h.y *= 0.25 + 0.75 * lift;
      _h.normalize();
      _b.copy(_p).addScaledVector(_h, AHEAD);
      this.parent.updateWorldMatrix(true, false);
      this.parent.worldToLocal(_b);
      this.parent.getWorldQuaternion(_q2).invert();
      _q3.setFromUnitVectors(X, _h).premultiply(_q2);
      this.frank.position.lerpVectors(pp, _b, this.held);
      this.frank.quaternion.slerpQuaternions(pq, _q3, this.held);
    } else {
      this.frank.position.copy(pp); this.frank.quaternion.copy(pq);
    }
    this.frank.updateMatrixWorld(true);
    this.placeCut();
  }

  /** the clip plane + cap at the bitten end, wherever the frank is now */
  private placeCut() {
    // the cut: a plane across the frank, BITE in from the bitten end per bite
    if (this.bites > 0) {
      const cut = LEN / 2 - this.bites * BITE;
      _h.set(this.end, 0, 0).transformDirection(this.frank.matrixWorld); // outward normal at the bitten end
      const at = _p.set(this.end * cut, 0, 0).applyMatrix4(this.frank.matrixWorld);
      // three clips where normal·p + constant < 0: the normal points back into the stub to keep it
      this.clip.normal.copy(_h).negate();
      this.clip.constant = -this.clip.normal.dot(at);
      this.cap.visible = true;
      this.cap.position.copy(this.parent.worldToLocal(at.clone()));
      const n = _h.clone();
      this.cap.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), n);
      const pq = this.parent.getWorldQuaternion(new THREE.Quaternion()).invert();
      this.cap.quaternion.premultiply(pq);
    }
  }
}
